/**
 * Every SharePoint identifier the app knows, in one file.
 *
 * This exists because these values have already changed once: the platform was
 * first provisioned on /sites/Engineering and moved to its own site, which
 * invalidated all thirteen GUIDs. Keeping them here makes that a one-file edit.
 * Do not reference a list by GUID or title anywhere else in the codebase.
 */

export const SITE = {
  hostname: 'yetmy.sharepoint.com',
  serverRelativeUrl: '/sites/CxPlatform',
  absoluteUrl: 'https://yetmy.sharepoint.com/sites/CxPlatform',
} as const;

export const AUTH = {
  clientId: '733ae8e2-a354-4721-a4e6-8ef82b9b5a9f',
  tenantId: 'e44a3569-9a30-4a7d-a3c7-3af9625f0f5e',
  authority: 'https://login.microsoftonline.com/e44a3569-9a30-4a7d-a3c7-3af9625f0f5e',

  /**
   * SharePoint REST and Microsoft Graph are different resources and need
   * different tokens. This app talks to `/_api/web/lists/...`, which is
   * SharePoint REST — a Graph token is rejected there, and the symptom is a bare
   * 401 with no hint that the audience is wrong.
   *
   * So: SharePoint scopes for everything the data layer does, Graph scopes only
   * for the signed-in user's name.
   */
  sharePointScopes: [`${'https://yetmy.sharepoint.com'}/AllSites.Manage`] as const,
  graphScopes: ['User.Read'] as const,

  /** Set at deploy time; must match a redirect URI registered on the app. */
  redirectUri: (globalThis as { location?: { origin?: string } }).location?.origin ?? '',
} as const;

export const LISTS = {
  projects: { title: 'Cx Projects', id: 'c9ac95a1-ae0a-4322-b481-919fde8ed948' },
  templates: { title: 'Cx Templates', id: '36f9085c-5c2a-4483-af09-446e25a62dae' },
  templateSections: { title: 'Cx Template Sections', id: '8e38403b-7d64-4b30-bf49-14cedf9f90c6' },
  templateItems: { title: 'Cx Template Items', id: '976e9f2d-022a-49ca-a636-f604c2c2ecd0' },
  assets: { title: 'Cx Assets', id: '974dcb28-cb83-46d2-acd9-cad63d561f5b' },
  instances: { title: 'Cx Instances', id: 'df3eac80-351a-4e13-b6a6-1eef5a6c9bd5' },
  resultLines: { title: 'Cx Result Lines', id: 'dd54b7ca-c21b-4089-8f9a-4b7a4c8a4fef' },
  issues: { title: 'Cx Issues', id: '919aa348-2f28-45f1-b665-d2f46424bcca' },
  instruments: { title: 'Cx Instruments', id: '6aff67c4-b902-4b23-ac4a-d74dd86203ef' },
  signoffs: { title: 'Cx Signoffs', id: '54aae1ef-43a7-4986-a395-606bcba10f08' },
  shotProfiles: { title: 'Cx Shot Profiles', id: '1f0a1c20-b026-4228-bd5e-8c6e7f27e34a' },
  meterVerification: { title: 'Cx Meter Verification', id: 'd5dcb90f-5643-423b-ab6d-75b5e361e35f' },
  evidence: { title: 'Cx Evidence', id: 'c2571a73-21e8-4ca2-847b-7dc2f4114e29' },
  changeRequests: { title: 'Cx Change Requests', id: 'e47cb99a-8fb6-4d0a-894c-57a412157e2b' },
} as const;

export type ListKey = keyof typeof LISTS;

/** Photo library layout: <ProjectCode>/<AssetTag>/<ShotType>/ */
export const EVIDENCE_PATH = (projectCode: string, assetTag: string, shotType: string): string =>
  `${projectCode}/${assetTag}/${shotType}`;

/** QR label payload, e.g. YETCX:MY03:MY03-E-B-GF-LVRA-MSB-1A */
export const QR_PREFIX = 'YETCX';
